import { CaptionPage as CaptionPageType } from "../CaptionGrouper";
import { fitText } from "@remotion/layout-utils";
import { useCurrentFrame, useVideoConfig, spring, interpolate } from "remotion";
import { Position, VideoCanvas, getSafeZonedWidth } from "../../DesignSystem";
import React from 'react';
import { FONT_MAP, loadFontByValue, FontValue } from "../../../types/fonts";

export interface CaptionPageProps {
    page: CaptionPageType;
    fontFamily: FontValue;
    primaryColor: string
    currentSpokenWordColor: string;
    maxFontSize: number;
    position: Position;
    canvas: VideoCanvas;
    outline: 'none' | 'glow' | 'shadow' | '3d-shadow' | 'stroke' | '3d-stroke';
    background: 'none' | 'white'|'colored' | 'translucent';
    textAnimation: 'none' | 'highlight' | 'boxed' | 'scale' | 'pop'| 'karaoke'| 'reveal'| 'reveal_up'|'reveal_scale'|'reveal_box'|'karaoke_scale'|'karaoke_box';
    borderRadius: number;
    captionCase: 'standard' | 'uppercase';
    hidePunctuation: boolean;
    containerPadding?: {
        x: number; // Horizontal padding in pixels
        y: number; // Vertical padding in pixels
    };
    boxPadding?: {
        x: number; // Horizontal padding in pixels
        y: number; // Vertical padding in pixels
    };
}

// Replace the fontMap constant with FONT_MAP from our centralized configuration
const fontMap = FONT_MAP;

// Create a wrapper component to handle font loading
const FontLoader: React.FC<{ fontFamily: FontValue; text: string; children: React.ReactNode }> = ({ fontFamily, text, children }) => {
    React.useEffect(() => {
        loadFontByValue(fontFamily, { text });
    }, [fontFamily, text]);
    return <>{children}</>;
};

const backgroundToCSS = (background: CaptionPageProps["background"], primaryColor: CaptionPageProps["primaryColor"]) => {
    switch (background) {
        case 'none':
            return 'transparent';
        case 'white':
            return 'rgba(255, 255, 255, 1)';
        case 'colored':
            return primaryColor;
        case 'translucent':
            return 'rgba(0, 0, 0, 0.7)';
    }
}

// Update the contrast calculation function to favor white text
const getContrastColor = (hexColor: string): string => {
    hexColor = colorNameToHex(hexColor);
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance using the formula for relative luminance in sRGB space
    // This follows the W3C accessibility guidelines
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Use a higher threshold to favor white text
    // Only use black text for very light colors (luminance > 0.7)
    return luminance > 0.7 ? 'black' : 'white';
};

const isTextWithOutline =(outline: CaptionPageProps["outline"]):boolean =>{
    return outline === '3d-shadow'|| outline === 'stroke' || outline === '3d-stroke';
}

function colorNameToHex(color: string): string {
    const colors: Record<string, string> = {
        black: "#000000",
        white: "#FFFFFF",
        red: "#FF0000",
        green: "#008000",
        blue: "#0000FF",
        yellow: "#FFFF00",
        cyan: "#00FFFF",
        magenta: "#FF00FF",
        gray: "#808080",
        orange: "#FFA500",
        purple: "#800080",
        pink: "#FFC0CB",
        brown: "#A52A2A"
    };
    return colors[color.toLowerCase()] || color; // Return hex if found, otherwise assume it's already a hex value
}

// Function to convert RGB to luminance
function rgbToLuminance(r: number, g: number, b: number): number {
    const a = [r / 255, g / 255, b / 255];
    for (let i = 0; i < 3; i++) {
        if (a[i] <= 0.03928) {
            a[i] /= 12.92;
        } else {
            a[i] = Math.pow((a[i] + 0.055) / 1.055, 2.4);
        }
    }
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

// Function to calculate contrast ratio
function calculateContrastRatio(luminance1: number, luminance2: number): number {
    const light = Math.max(luminance1, luminance2);
    const dark = Math.min(luminance1, luminance2);
    return (light + 0.05) / (dark + 0.05);
}

// Function to check contrast and return result as JSON
function checkContrast(fixedColor: string, changeableColor: string): {
    sufficientContrast: boolean,
    replacementColor: string
} {
     // Convert color name to hex if needed
     fixedColor = colorNameToHex(fixedColor);
     changeableColor = colorNameToHex(changeableColor);
    // Convert hex color to RGB
    function hexToRgb(hex: string): [number, number, number] {
        let r: number, g: number, b: number;
        if (hex.startsWith("#")) {
            hex = hex.slice(1);
        }
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
        return [r, g, b];
    }

    // Convert hex colors to RGB
    const [fixedR, fixedG, fixedB] = hexToRgb(fixedColor);
    const [changeableR, changeableG, changeableB] = hexToRgb(changeableColor);

    // Calculate luminance for both colors
    const luminanceFixed = rgbToLuminance(fixedR, fixedG, fixedB);
    const luminanceChangeable = rgbToLuminance(changeableR, changeableG, changeableB);

    // Calculate contrast ratio
    const contrast = calculateContrastRatio(luminanceFixed, luminanceChangeable);

    // Check if the contrast is sufficient
    if (contrast < 4.5) {
        // Calculate contrast with black (#000000) and white (#FFFFFF)
        const luminanceBlack = rgbToLuminance(0, 0, 0);
        const luminanceWhite = rgbToLuminance(255, 255, 255);
        const contrastWithBlack = calculateContrastRatio(luminanceFixed, luminanceBlack);
        const contrastWithWhite = calculateContrastRatio(luminanceFixed, luminanceWhite);

        // Return the JSON object with replacement color
        return {
            sufficientContrast: false,
            replacementColor: contrastWithBlack > contrastWithWhite ? "black" : "white"
        };
    }

    // If the contrast is sufficient, return JSON with sufficient contrast
    return {
        sufficientContrast: true,
        replacementColor: "black"
    };
}

const isTextAnimationWithBox = (textAnimation: CaptionPageProps["textAnimation"]): boolean =>{
    return textAnimation === 'boxed' || textAnimation === 'karaoke_box' || textAnimation === 'reveal_box';
}

const isTextAnimationWithTextHighlightColor = (textAnimation: CaptionPageProps["textAnimation"]): boolean =>{
    return !isTextAnimationWithBox(textAnimation) && textAnimation !== 'none';
}

/**
 * Calculate the maximum scale factor for text animations
 * Used to ensure SVG containers are sized appropriately for scaled text
 */
const getMaxScaleFactor = (textAnimation: CaptionPageProps["textAnimation"]): number => {
    // Scale animations that scale to 1.1 (110%)
    if (textAnimation === 'scale' || 
        textAnimation === 'reveal_scale' || 
        textAnimation === 'karaoke_scale') {
        return 1.1;
    }
    
    // Pop animation scales to 1.25 (125%) temporarily
    if (textAnimation === 'pop') {
        return 1.25;
    }
    
    // No scaling for other animations
    return 1.0;
};

const getOutlineAndTextColorForStyle = (
    outline: CaptionPageProps["outline"],
    textAnimation: CaptionPageProps["textAnimation"],
    active: boolean,
    background: CaptionPageProps["background"],
    currentSpokenWordColor: string,
    primaryColor: string
): { outlineColor: string|undefined; textColor: string } => {
    const hasOutline =  isTextWithOutline(outline);
    
    let backgroundColor = background === 'none'? undefined:( background === "translucent"?'black':(background === 'white'?'white': primaryColor));
    let outlineColor = hasOutline?primaryColor: undefined;



    if(outlineColor && backgroundColor){
        if(outlineColor === backgroundColor){
            outlineColor = getContrastColor(backgroundColor);
        }
        const backgroundToOutlineContrastAnalysis = checkContrast(backgroundColor, outlineColor);
        if(!backgroundToOutlineContrastAnalysis.sufficientContrast){
            outlineColor = backgroundToOutlineContrastAnalysis.replacementColor;
        }
    }
    let textColor = primaryColor;
    if(textColor === backgroundColor && backgroundColor=== primaryColor){
        textColor = getContrastColor(backgroundColor);
        if(outlineColor && outlineColor === textColor){
            textColor = getContrastColor(outlineColor);
        }
    }
    if(textColor === outlineColor){
        textColor = getContrastColor(outlineColor);
    }
    if(!active){
        return {outlineColor, textColor};
    }else{
        if(isTextAnimationWithBox(textAnimation)){
            if(!outlineColor){
                const highlightToTextColorContrastAnalysis = checkContrast(currentSpokenWordColor, textColor);
                if(!highlightToTextColorContrastAnalysis.sufficientContrast){
                    textColor = highlightToTextColorContrastAnalysis.replacementColor;
                }
            }
            return {outlineColor, textColor};
        }
        if(isTextAnimationWithTextHighlightColor(textAnimation)){
            textColor = currentSpokenWordColor;
            if(outlineColor){
                const outlineToTextColorContrastAnalysis = checkContrast(outlineColor, textColor);
                if(!outlineToTextColorContrastAnalysis.sufficientContrast){
                    outlineColor = currentSpokenWordColor;
                    textColor = getContrastColor(outlineColor);
                }
            }
        }
        return {outlineColor, textColor};
    }
}

// Helper function to calculate colors and active state for a token
const getTokenRenderInfo = (
    token: any,
    index: number,
    timeInMs: number,
    pageStartMs: number,
    textAnimation: CaptionPageProps["textAnimation"],
    outline: CaptionPageProps["outline"],
    background: CaptionPageProps["background"],
    currentSpokenWordColor: string,
    primaryColor: string
) => {
    const startRelativeToSequence = token.fromMs - pageStartMs;
    const endRelativeToSequence = token.toMs - pageStartMs;
    const active = startRelativeToSequence <= timeInMs && endRelativeToSequence > timeInMs;

    // Calculate colors in the same cycle as active state
    const { outlineColor, textColor } = getOutlineAndTextColorForStyle(
        outline,
        textAnimation,
        active,
        background,
        currentSpokenWordColor,
        primaryColor
    );

    return {
        active,
        outlineColor,
        textColor,
        startRelativeToSequence,
        endRelativeToSequence
    };
};

export type OutlineType = 'none' | 'glow' | 'shadow' | '3d-shadow' | 'stroke' | '3d-stroke';
export type LayerType = 'glow' | 'main' | 'other';

// Utility function to calculate actual border radius
export const calculateActualBorderRadius = (fontSize: number, borderRadius: number): number => {
    return fontSize * borderRadius;
};

export const getKaraokeOpacity = (fromMs: number, pageStartMs: number, timeInMs: number): number => {
    const progress = (timeInMs - (fromMs - pageStartMs)) / 200;
    return Math.max(0, Math.min(1, progress));
};

export const getRevealProgress = (fromMs: number, toMs: number, pageStartMs: number, timeInMs: number): number => {
    const progress = (timeInMs - (fromMs - pageStartMs)) / (toMs - fromMs);
    return Math.max(0, Math.min(1, progress));
};

export interface OutlineRenderProps {
    outline: OutlineType;
    layerType: LayerType;
    token: any;
    index: number;
    positions: Array<{ x: number; width: number }>;
    verticalPosition: number;
    fontSize: number;
    fontFamily: string;
    outlineColor?: string;
    opacity: number;
    combinedTransform: string;
    processText: (text: string) => string;
    textAnchor?: 'start' | 'middle' | 'end';
}

export const renderOutline = ({
    outline,
    layerType,
    token,
    index,
    positions,
    verticalPosition,
    fontSize,
    fontFamily,
    outlineColor,
    opacity,
    combinedTransform,
    processText
}: OutlineRenderProps) => {
    if (outline === 'none') {
        return null;
    }

    const text = processText(token.text);
    const x = positions[index].x + (positions[index].width / 2); // Center x position

    if (outline === 'glow' && layerType === 'glow') {
        if (opacity === 0) return null;
        return [
            <text
                key={`glow1-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.3)"
                transform={`${combinedTransform} translate(3,3)`}
                filter="blur(10px)"
                opacity={opacity * 0.3}
            >
                {text}
            </text>,
            <text
                key={`glow2-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.4)"
                transform={`${combinedTransform} translate(1.5,1.5)`}
                filter="blur(5px)"
                opacity={opacity * 0.4}
            >
                {text}
            </text>,
            <text
                key={`glow3-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.5)"
                transform={`${combinedTransform} translate(1,1)`}
                filter="blur(3px)"
                opacity={opacity * 0.5}
            >
                {text}
            </text>
        ];
    }

    if (outline === 'shadow' && layerType === 'other') {
        if (opacity === 0) return null;
        return [
            <text
                key={`shadow1-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(1.5,1.5)`}
                filter="blur(3px)"
                opacity={opacity * 0.15}
                stroke="black"
                strokeWidth={fontSize * 0.015}
            >
                {text}
            </text>,
            <text
                key={`shadow2-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(0.5,0.5)`}
                filter="blur(1.5px)"
                opacity={opacity * 0.2}
                stroke="black"
                strokeWidth={fontSize * 0.004}
            >
                {text}
            </text>,
            <text
                key={`shadow3-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(0.3,0.3)`}
                filter="blur(0.8px)"
                opacity={opacity * 0.25}
                stroke="black"
                strokeWidth={fontSize * 0.001}
            >
                {text}
            </text>
        ];
    }

    if (outline === '3d-shadow' && layerType === 'other') {
        if (opacity === 0) return null;
        if (!outlineColor) return null;

        return Array.from({ length: 4 }).map((_, i) => {
            const offset = (fontSize * 0.06 / 4) * (i + 1);
            const baseLayerOpacity = 0.5 + (i * 0.125);
            const finalLayerOpacity = baseLayerOpacity * opacity;

            return (
                <text
                    key={`shadow-layer-${i}-${token.fromMs}-${index}`}
                    x={x}
                    y={verticalPosition + offset}
                    dominantBaseline="middle"
                    textAnchor="middle"
                    style={{ fontSize, fontFamily }}
                    fill={outlineColor}
                    stroke={outlineColor}
                    strokeWidth={fontSize * 0.06}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    opacity={finalLayerOpacity}
                    transform={combinedTransform}
                >
                    {text}
                </text>
            );
        });
    }

    if ((outline === 'stroke' || outline === '3d-stroke') && layerType === 'other') {
        if (opacity === 0) return null;
        if (!outlineColor) return null;

        if (outline === '3d-stroke') {
            return Array.from({ length: 4 }).map((_, i) => {
                const offset = (fontSize * 0.06 / 4) * (i + 1);
                return (
                    <text
                        key={`3d-stroke-layer-${i}-${token.fromMs}-${index}`}
                        x={x}
                        y={verticalPosition + offset}
                        dominantBaseline="middle"
                        textAnchor="middle"
                        style={{ fontSize, fontFamily }}
                        fill="none"
                        stroke={outlineColor}
                        strokeWidth={fontSize * 0.18}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        opacity={opacity}
                        transform={combinedTransform}
                    >
                        {text}
                    </text>
                );
            });
        }

        return (
            <text
                key={`stroke-${token.fromMs}-${index}`}
                x={x}
                y={verticalPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontSize, fontFamily }}
                stroke={outlineColor}
                strokeWidth={fontSize * 0.12}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
                opacity={opacity}
                transform={combinedTransform}
            >
                {text}
            </text>
        );
    }

    return null;
};

export const renderTokens = (
    layerType: 'glow' | 'main' | 'other',
    tokens: any[],
    positions: Array<{ x: number; width: number }>,
    baseVerticalPosition: number,
    fontSize: number,
    fontFamily: string,
    processText: (text: string) => string,
    textAnimation: CaptionPageProps["textAnimation"] = 'none',
    outline: CaptionPageProps["outline"] = 'none',
    background: CaptionPageProps["background"] = 'none',
    currentSpokenWordColor: string = '#000000',
    primaryColor: string = '#000000',
    timeInMs: number = 0,
    pageStartMs: number = 0,
    fps: number // Added fps parameter
) => {
    return tokens.map((t: any, index) => {
        const renderInfo = getTokenRenderInfo(
            t,
            index,
            timeInMs,
            pageStartMs,
            textAnimation,
            outline,
            background,
            currentSpokenWordColor,
            primaryColor
        );

        const { active, outlineColor, textColor, startRelativeToSequence, endRelativeToSequence } = renderInfo;

        // Calculate base transforms
        const wordWidth = positions[index].width;
        const wordX = positions[index].x;
        const wordCenterX = wordX + (wordWidth / 2);
        const verticalPosition = baseVerticalPosition + (t.verticalOffset !== undefined ? t.verticalOffset : 0);

        let scale = 1;
        let revealTransform = '';
        let animatedOpacity = 1; // Opacity controlled by animations like reveal

        if (active) {
            const timeSinceTokenActiveMs = timeInMs - startRelativeToSequence;
            const framesSinceTokenActive = Math.max(0, Math.floor(timeSinceTokenActiveMs / (1000 / fps)));

            if (textAnimation === 'pop') {
                const popDurationFrames = 15;
                const progress = spring({
                    frame: framesSinceTokenActive,
                    fps: fps,
                    config: { damping: 15, stiffness: 180, mass: 0.7 },
                    durationInFrames: popDurationFrames,
                });
                scale = interpolate(progress, [0, 0.5, 1], [1, 1.25, 1]);
            } else if (textAnimation === 'scale' ||
                       textAnimation === 'reveal_scale' ||
                       textAnimation === 'karaoke_scale') {
                scale = spring({
                    frame: framesSinceTokenActive,
                    fps: fps,
                    from: 1,
                    to: 1.1, // Changed from 1.05 to 1.1
                    durationInFrames: 20,
                    config: {
                        damping: 20,
                        stiffness: 100,
                        mass: 0.6
                    }
                });
            }

            if (textAnimation === 'reveal' || textAnimation === 'reveal_up' || textAnimation === 'reveal_scale' || textAnimation === 'reveal_box') {
                const revealDurationFrames = 12;
                if (framesSinceTokenActive < revealDurationFrames) { // Animation in progress
                    const slideAmount = spring({
                        frame: framesSinceTokenActive,
                        fps: fps,
                        durationInFrames: revealDurationFrames,
                        config: { damping: 15, stiffness: 200, mass: 0.6 }
                    });
                    if (textAnimation === 'reveal_up') {
                        const slideY = interpolate(slideAmount, [0, 1], [fontSize * 0.3, 0]);
                        revealTransform = `translate(0, ${slideY})`;
                    } else {
                        const slideX = interpolate(slideAmount, [0, 1], [-fontSize * 0.3, 0]);
                        revealTransform = `translate(${slideX}, 0)`;
                    }
                    animatedOpacity = 1; // Text is fully opaque during slide-in
                } else { // Animation finished
                    revealTransform = '';
                    animatedOpacity = 1;
                }
            }
        } else { // Not active: handle future words for reveal animations
            if ((textAnimation === 'reveal' || textAnimation === 'reveal_up' || textAnimation === 'reveal_scale' || textAnimation === 'reveal_box') && timeInMs < startRelativeToSequence) {
                revealTransform = textAnimation === 'reveal_up'
                    ? `translate(0, ${fontSize * 0.3})`
                    : `translate(-${fontSize * 0.3}, 0)`;
                animatedOpacity = 0;
            } else {
                // Past words or non-reveal non-active words
                revealTransform = '';
                animatedOpacity = 1; // Default to visible if not a future reveal word
            }
        }

        const scaleTransform = scale !== 1
            ? `translate(${wordCenterX} ${verticalPosition}) scale(${scale}) translate(${-wordCenterX} ${-verticalPosition})`
            : '';

        const combinedTransform = [scaleTransform, revealTransform].filter(Boolean).join(' ');

        const karaokeOpacityValue = (textAnimation === 'karaoke' || textAnimation === 'karaoke_box' || textAnimation === 'karaoke_scale')
            ? getKaraokeOpacity(t.fromMs, pageStartMs, timeInMs)
            : 1;

        let finalOpacity;
        if (textAnimation === 'karaoke' || textAnimation === 'karaoke_box' || textAnimation === 'karaoke_scale') {
            finalOpacity = karaokeOpacityValue;
        } else {
            finalOpacity = animatedOpacity; // Use opacity from reveal or default 1
        }

        const outlineElements = renderOutline({
            outline,
            layerType,
            token: t,
            index,
            positions,
            verticalPosition,
            fontSize,
            fontFamily,
            outlineColor,
            opacity: finalOpacity,
            combinedTransform,
            processText
        });

        if (outlineElements) return outlineElements;

        if (layerType === 'main') {
            let textRenderOpacity = finalOpacity;
            // Karaoke has specific opacity rules for active/past/future words
            if (textAnimation === 'karaoke' ||
                textAnimation === 'karaoke_box' ||
                textAnimation === 'karaoke_scale') {

                if (active) { // 'active' is from getTokenRenderInfo
                    textRenderOpacity = 1;
                } else {
                    // 'startRelativeToSequence' and 'endRelativeToSequence' are from getTokenRenderInfo
                    // 'timeInMs' is a parameter to renderTokens
                    if (timeInMs < startRelativeToSequence) { // Future word
                        textRenderOpacity = 0.4;
                    } else { // Past word (since not active and not future)
                        textRenderOpacity = 1;
                    }
                }
            }

            return (
                <text
                    key={`text-${t.fromMs}-${index}`}
                    x={positions[index].x}
                    y={verticalPosition}
                    fill={textColor}
                    opacity={textRenderOpacity}
                    dominantBaseline="middle"
                    style={{ fontSize, fontFamily }}
                    transform={combinedTransform}
                >
                    {processText(t.text)}
                </text>
            );
        }

        return null;
    }).flat();
};

// Modify the main component to accept video config and current frame as props
export const ClassicCaptionPage: React.FC<CaptionPageProps & { videoConfig?: any; currentFrame?: number }> = ({
    page,
    fontFamily,
    canvas,
    primaryColor,
    currentSpokenWordColor,
    maxFontSize,
    outline,
    background,
    textAnimation,
    borderRadius,
    captionCase,
    hidePunctuation,
    containerPadding = { x: 0.1, y: 0.05 },
    boxPadding = { x: 0, y: 0 },
    videoConfig,
    currentFrame
}) => {
    // Use provided video config or get it from hook
    const { fps, width } = videoConfig || useVideoConfig();
    const frame = currentFrame ?? useCurrentFrame();
    const timeInMs = frame / fps * 1000;

    const safeZonedWidth = getSafeZonedWidth(width, canvas)

    const processText = (text: string): string => {
        // First trim any leading/trailing spaces and remove quotes
        let processedText = text.trim().replace(/^"|"$/g, '');
        
        // Process the text
        if (hidePunctuation) {
            processedText = processedText.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '');
        }
        if (captionCase === 'uppercase') {
            processedText = processedText.toUpperCase();
        }
        return processedText;
    }

    const fittedText = fitText({
        fontFamily: fontMap[fontFamily],
        text: processText(page.text),
        withinWidth: safeZonedWidth * 0.9,
    });

    const fontSize = Math.min(maxFontSize, fittedText.fontSize);

    // Calculate actual padding in pixels based on fontSize
    const actualBoxPadding = {
        x: fontSize * boxPadding.x,
        y: fontSize * boxPadding.y
    };

    const actualContainerPadding = {
        x: fontSize * containerPadding.x,
        y: fontSize * containerPadding.y
    };

    // Convert borderRadius to be fontSize-based
    const actualBorderRadius = fontSize * borderRadius;

    // Calculate the total width including spaces
    const getTotalWidth = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return 0;

        context.font = `${fontSize}px ${fontMap[fontFamily]}`;
        
        // Calculate total width with font-specific spacing
        return page.tokens.reduce((totalWidth, token, index) => {
            const text = processText(token.text);
            const wordWidth = context.measureText(text).width;
            
            // Add font-specific spacing between words
            // For wider fonts, increase the space proportionally
            const spaceWidth = context.measureText(' ').width;
            const fontSpecificSpace = index > 0 ? spaceWidth * 1.2 : 0; // 20% more space between words
            
            return totalWidth + wordWidth + fontSpecificSpace;
        }, 0);
    };

    const totalWidth = getTotalWidth();
    
    // Calculate extra space needed for text effects - only for x-axis
    const effectsPadding = (() => {
        if (outline === '3d-shadow' || outline === '3d-stroke') {
            return fontSize * 0.15;
        } else if (outline === 'stroke') {
            return fontSize * 0.1;
        } else if (outline === 'shadow' || outline === 'glow') {
            return fontSize * 0.08; // Increased from 0.05 for glow/shadow
        }
        return 0;
    })();

    // Only add base padding if we have container padding - only for x-axis
    const basePadding = containerPadding.x === 0
        ? (outline === 'none' ? 0 : fontSize * 0.05) // Increased from 0.03
        : fontSize * 0.1; // Increased from 0.08

    const svgPadding = basePadding + effectsPadding;
    
    // Calculate maximum scale factor for current animation to ensure container accommodates scaled text
    const scaleFactor = getMaxScaleFactor(textAnimation);
    
    // Calculate base container width with scale buffer
    const baseContainerWidth = totalWidth + 
        (actualContainerPadding.x * 2) + // Container padding
        (svgPadding * 2) + // Effects padding
        (fontSize * 0.1); // Base padding to prevent cutoff
    const containerWidth = baseContainerWidth * scaleFactor;



    
    // Calculate SVG height more precisely with tighter dimensions
    const svgHeight = (() => {
        // Base height calculation - use original proportions
        let baseHeight = fontSize * 1.15; // Base height for single line
        
        // Add extra space for effects using original proportions
        if (outline === '3d-stroke') {
            baseHeight = fontSize * 1.25;
        } else if (outline === '3d-shadow') {
            baseHeight = fontSize * 1.2;
        } else if (outline === 'shadow') {
            baseHeight = fontSize * 1.15;
        } else if (outline === 'stroke') {
            baseHeight = fontSize * 1.15;
        } else if (outline === 'glow') {
            baseHeight = fontSize * 1.15;
        } else {
            baseHeight = fontSize * 1.1;
        }
        
        // Add container padding only if background exists
        if (background !== 'none') {
            baseHeight += (actualContainerPadding.y * 2);
        }
        
        // Apply scale buffer to height as well to accommodate scaled text
        return baseHeight * scaleFactor;
    })();

    // Define the getWordPositions function with proper container width for centering
    const getWordPositions = (finalContainerWidth: number) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return [];

        context.font = `${fontSize}px ${fontMap[fontFamily]}`; // Fixed font family reference
        
        const words = page.tokens.map(t => processText(t.text));
        // Calculate font-specific space width
        const baseSpaceWidth = context.measureText(' ').width;
        const spaceWidth = baseSpaceWidth * 1.2; // Add 20% more space for better readability
        
        // Calculate total width including adjusted spaces between words
        const totalTextWidth = words.reduce((acc, word, i) => {
            const wordWidth = context.measureText(word).width;
            return acc + wordWidth + (i > 0 ? spaceWidth : 0);
        }, 0);
        
        // Center the text block within the final container (with scale factor applied)
        const startX = (finalContainerWidth - totalTextWidth) / 2;
        
        let currentX = startX;
        const positions = page.tokens.map((token, index) => {
            if (index > 0) {
                currentX += spaceWidth;
            }

            const text = processText(token.text);
            const width = context.measureText(text).width;
            const position = {
                x: currentX,
                width: width
            };
            currentX += width;

            return position;
        });

        return positions;
    };

    // First, let's improve the centerY calculation in the existing code
    const centerY = (() => {
        // Base center position
        let center = svgHeight / 2;
        
        // Adjust vertical position for different outline styles
        if (outline === '3d-stroke') {
            center = svgHeight / 2 - fontSize * 0.02; // Already optimized
        } else if (outline === '3d-shadow') {
            center = svgHeight / 2 - fontSize * 0.02; // Reduced from 0.05 for less upward shift
        }
        
        return center;
    })();


    // Calculate positions once at the top level using the final scaled container width
    const positions = getWordPositions(containerWidth);

    return (
        <FontLoader fontFamily={fontFamily} text={page.text}>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
            }}>
                <div style={{
                    display: 'inline-block',
                    backgroundColor: background !== 'none' ? backgroundToCSS(background, primaryColor) : 'transparent',
                    borderRadius: actualBorderRadius,
                    paddingLeft: background !== 'none' ? actualContainerPadding.x : 0,
                    paddingRight: background !== 'none' ? actualContainerPadding.x : 0,
                    paddingTop: background !== 'none' ? actualContainerPadding.y : 0,
                    paddingBottom: background !== 'none' ? actualContainerPadding.y : 0,
                    overflow: 'hidden',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                }}>
                    <svg 
                        style={{
                            width: 'auto',
                            height: 'auto',
                            display: 'block',
                        }}
                        width={containerWidth}
                        height={svgHeight}
                        viewBox={`0 0 ${containerWidth} ${svgHeight}`}
                        preserveAspectRatio="xMidYMid meet"
                    >
                        {/* 1. Background boxes */}
                        {(textAnimation === 'boxed' || textAnimation === 'reveal_box' || textAnimation === 'karaoke_box') && 
                            page.tokens.map((t, index) => {
                                const renderInfo = getTokenRenderInfo(
                                    t,
                                    index,
                                    timeInMs,
                                    page.startMs,
                                    textAnimation,
                                    outline,
                                    background,
                                    currentSpokenWordColor,
                                    primaryColor
                                );
                                const { active, startRelativeToSequence } = renderInfo;

                                if (!active) { // Strict: only render for active words
                                    return null;
                                }

                                // If active, proceed to calculate transforms and render with opacity 1
                                const wordWidth = positions[index].width;
                                const outlinePadding = (() => {
                                    if (outline === '3d-shadow') return fontSize * 0.15;
                                    if (outline === '3d-stroke') return fontSize * 0.18;
                                    if (outline === 'stroke') return fontSize * 0.12;
                                    if (outline === 'shadow' || outline === 'glow') return fontSize * 0.08;
                                    return 0;
                                })();

                                let boxHeight, boxY;
                                if (outline === '3d-shadow') {
                                    boxHeight = fontSize * 1.15 + (actualBoxPadding.y * 2);
                                    boxY = centerY - (fontSize * 0.55) - actualBoxPadding.y;
                                } else if (outline === '3d-stroke') {
                                    boxHeight = fontSize * 1.2 + (actualBoxPadding.y * 2);
                                    boxY = centerY - (fontSize * 0.58) - actualBoxPadding.y;
                                } else if (outline === 'stroke') {
                                    boxHeight = fontSize * 1.1 + (actualBoxPadding.y * 2);
                                    boxY = centerY - (fontSize * 0.55) - actualBoxPadding.y;
                                } else {
                                    boxHeight = fontSize * 1.10 + (actualBoxPadding.y * 2);
                                    boxY = centerY - (fontSize * 0.55) - actualBoxPadding.y;
                                }

                                const totalBoxPadding = { x: actualBoxPadding.x + outlinePadding, y: actualBoxPadding.y };

                                let boxTransform = '';
                                const framesSinceTokenActive = Math.max(0, Math.floor((timeInMs - startRelativeToSequence) / (1000 / fps)));

                                const scaleAmount = spring({
                                    frame: framesSinceTokenActive,
                                    fps,
                                    from: 0.8,
                                    to: 1,
                                    durationInFrames: 10,
                                    config: { damping: 12, stiffness: 180, mass: 0.5 }
                                });
                                const boxCenterX = positions[index].x + wordWidth / 2;
                                const scaleTransform = `translate(${boxCenterX}, ${centerY}) scale(${scaleAmount}) translate(${-boxCenterX}, ${-centerY})`;

                                if (textAnimation === 'reveal_box') {
                                    const revealDuration = 12;
                                    const slideAmountReveal = spring({
                                        frame: framesSinceTokenActive,
                                        fps: fps,
                                        durationInFrames: revealDuration,
                                        config: { damping: 15, stiffness: 200, mass: 0.6 }
                                    });
                                    const slideX = interpolate(slideAmountReveal, [0, 1], [-fontSize * 0.3, 0]);
                                    boxTransform = `${scaleTransform} translate(${slideX}, 0)`;
                                } else {
                                    boxTransform = scaleTransform;
                                }

                                return (
                                    <rect
                                        key={`box-${t.fromMs}-${index}`}
                                        x={positions[index].x - totalBoxPadding.x}
                                        y={boxY}
                                        width={wordWidth + (totalBoxPadding.x * 2)}
                                        height={boxHeight}
                                        fill={currentSpokenWordColor}
                                        rx={actualBorderRadius}
                                        ry={actualBorderRadius}
                                        transform={boxTransform}
                                        opacity={1} // Hardcoded to 1 for active boxes
                                    />
                                );
                            })
                        }

                        {/* 2. Shadow effect */}
                        {outline === 'shadow' && renderTokens('other', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}

                        {/* 3. 3D Shadow layers */}
                        {outline === '3d-stroke' && renderTokens('other', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}

                        {/* 3. 3D Shadow layers */}
                        {outline === '3d-shadow' && renderTokens('other', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}

                        {/* 4. Stroke layer */}
                        {(outline === 'stroke') && renderTokens('other', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}

                        {/* 5. Glow effect */}
                        {outline === 'glow' && renderTokens('glow', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}

                        {/* 6. Main text - Always last */}
                        {renderTokens('main', page.tokens, positions, centerY, fontSize, fontMap[fontFamily], processText, textAnimation, outline, background, currentSpokenWordColor, primaryColor, timeInMs, page.startMs, fps)}
                    </svg>
                </div>
            </div>
        </FontLoader>
    );
}
